import { useErrorBoundary } from 'react-error-boundary';
import { clearPnpSessionStorage, getDepositAmount } from '../../components/trustlyIframe/trustlyUtils.ts';
import { ROUTE_IDS } from '../../constants.ts';
import { BeErrorEnvelope, parseBeError, toIsoTimestamp } from '../../flow/errors/beErrorResponse.ts';
import { buildAppError } from '../../flow/errors/errorBuilder.ts';
import { FE_ERROR_CODES, FeErrorCode, mapBeReasonToFe } from '../../flow/errors/errorCodes.ts';
import useMarketInfo from '../../hooks/useMarketInfo.tsx';
import { isRegistrationInFlight, TDynamicStoreField, useRegistrationStore } from '../../store/store.ts';
import { COOKIE_NAMES, HEADER_NAMES, OPTED_OUT_WELCOME_OFFER_ID } from '../../utils/constants.ts';
import { getCookieByName, storeInCookie } from '../../utils/cookies.ts';
import { FLOW_NAMES } from '../../utils/countries.ts';
import { CASUMO_BRAND_ID, REACT_APP_VERSION } from '../../utils/envConstants.ts';
import { gatekeeper, sendGtmEvent } from '../../utils/gatekeeper.ts';
import { GtmEvents } from '../../utils/gtm/gtm.ts';
import { setPlayerLoginNativeEvents, setRegistrationEndNativeEvents } from '../../utils/native.ts';
import { setPnpDepositErrorPopup } from '../../utils/pnpDepositErrorPopup.ts';
import { calculateTimeSpent } from '../../utils/timer.ts';
import {
  clearDataInLocalStorage,
  getCmapiUrl,
  getPnpRegisterUrl,
  getRegisterUrl,
  registrationCompleteTracking,
  setWelcomeLoginCookie,
} from '../../utils/utils.ts';
import { getRequestBody } from '../register/utils.ts';

/**
 * Optional: if you want to narrow the shape you expect from BE.
 */
type RegisterResponse = BeErrorEnvelope & {
  playerId: string;
  playerIdToken?: string;
  sessionId?: string;
  casumoName?: string;
  status?: string;
  reason?: string;
  allowedCheckCountExceeded?: boolean;
  loginResponse?: {
    reason?: string;
    identificationToken?: string;
    playerId?: string;
  };
  depositResult: {
    success: boolean;
    amount: string;
    currency: string;
    createdAt: number | string;
  };
};

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 3000;
const SEON_FIELD_TIMEOUT_MS = 2000;
const SEON_SESSION_TIMEOUT_MS = 5000;

const getSeonSession = async (): Promise<string> => {
  const timeout = new Promise<string>((resolve) => {
    setTimeout(() => resolve(''), SEON_SESSION_TIMEOUT_MS);
  });

  const pendingSession = window.seon?.getSession?.({ fieldTimeoutMs: SEON_FIELD_TIMEOUT_MS }) ?? '';
  const session = await Promise.race([pendingSession, timeout]).catch(() => '');

  if (!session) {
    sendGtmEvent({
      feature: 'register',
      action:  'seon_session_unavailable',
      data:    { timeout_ms: SEON_SESSION_TIMEOUT_MS },
    });
  }

  return session;
};

const useRegister = () => {
  const { formData, setMetadata } = useRegistrationStore.getState();
  const { market, marketCode, flowName } = useMarketInfo();
  const { setRootLoading, setRegisterError, isRegistering, setIsRegistering } = useRegistrationStore();
  const { showBoundary } = useErrorBoundary();
  const isPnpFlow = flowName === FLOW_NAMES.MGA_GRA_PNP;

  if (!setMetadata) {
    return {
      isRegistering,
      registerUser: async () => {
      },
    };
  }

  const url = isPnpFlow ? getPnpRegisterUrl(market) : getRegisterUrl(market);
  const cmapiUrl = getCmapiUrl(market);

  /**
   * Normalize server response to a single FE error code.
   * Handles your existing "INVALID" special cases.
   */
  const getFeErrorFromResponse = (data?: RegisterResponse): FeErrorCode => {
    if (!data) return FE_ERROR_CODES.unknown_error;

    const beError = parseBeError(data);
    if (beError) return beError.feCode;

    const beReason =
      data.reason ??
      data.loginResponse?.reason ??
      data.status ??
      undefined;

    if (data.status === 'INVALID' && data.allowedCheckCountExceeded) {
      return FE_ERROR_CODES.registration_cancelled;
    }
    if (data.status === 'INVALID' && !data.allowedCheckCountExceeded) {
      return FE_ERROR_CODES.incorrect_details;
    }

    return mapBeReasonToFe(beReason);
  };

  const registerUser = async (passedData: TDynamicStoreField) => {
    if (isRegistrationInFlight()) return;

    const requestBody = getRequestBody(passedData || formData);
    let failedAttempts = 0;
    let hasRegistered = false;

    const cmapiCookiePrivacy = getCookieByName(COOKIE_NAMES.CMAPI_COOKIE_PRIVACY) ?? '';

    setIsRegistering(true);
    setRootLoading(true);

    const attemptRegistration = async (attempt: number, seonSession: string): Promise<void> => {
      try {
        const response = await fetch(url, {
          method:  'POST',
          headers: {
            [HEADER_NAMES.CASUMO_BRAND_ID]: `${CASUMO_BRAND_ID}`,
            Accept:                         'application/json',
            'Content-Type':                 'application/json',
            [HEADER_NAMES.USER_AGENT]:      navigator?.userAgent ?? '',
          },
          body: JSON.stringify({ ...requestBody, seonSession }),
        });

        const data: RegisterResponse = await response.json();

        if (response.ok) {
          const playerIdToken = data?.playerIdToken;

          if (playerIdToken) {
            try {
              await fetch(cmapiUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ playerIdToken, cmapiCookiePrivacy }),
              });
            } catch (cmErr) {
              console.warn('CMAPI call failed:', cmErr);
            }
          }

          const registrationData = passedData || formData;
          const welcomeOffer = registrationData?.welcomeOffer;
          const optedInToWelcomeOffer = !!welcomeOffer && welcomeOffer !== OPTED_OUT_WELCOME_OFFER_ID;
          const shouldRedirectToLobby = optedInToWelcomeOffer || isPnpFlow;
          const redirectDestination = shouldRedirectToLobby ? 'lobby' : 'cashier';

          const timeSpentData = calculateTimeSpent();
          const depositAmount = getDepositAmount();
          sendGtmEvent({
            feature: 'register',
            action:  'registration_done',
            data:    {
              ...registrationCompleteTracking(registrationData, data?.playerId),
              variant:              `${gatekeeper?.localisation?.marketSlug}-${REACT_APP_VERSION}`,
              time_spent:           timeSpentData?.timeSpent,
              number_of_occasions:  timeSpentData?.numberOfOccasions,
              redirect_destination: redirectDestination,
              deposit_amount:       depositAmount ? Number(depositAmount) : undefined,
            },
          });

          storeInCookie(COOKIE_NAMES.FIRST_LOGIN, 'first_login', 'true');

          sendGtmEvent({
            feature: 'login',
            action:  'login.success',
            data:    {
              userId:         data?.playerId,
              is_first_login: true,
            },
          });

          //eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((window as any)?.native) {
            setRegistrationEndNativeEvents({
              woId:   formData?.welcomeOffer,
              userId: data?.playerId || '',
            });

            setPlayerLoginNativeEvents({
              userId:     data?.playerId,
              email:      formData?.email,
              marketCode: marketCode || '',
              sessionId:  data?.sessionId ?? '',
              username:   data?.casumoName ?? '',
            });
          }

          setWelcomeLoginCookie();
          if (isPnpFlow && data.depositResult?.success === false) {
            setPnpDepositErrorPopup({
              amount:    data.depositResult.amount,
              currency:  data.depositResult.currency,
              timestamp: toIsoTimestamp(data.depositResult.createdAt),
            });
          }

          clearDataInLocalStorage(market);
          // TODO: Also we need to check if BE session_expired we should clear pnp session from sessions storage.
          if (isPnpFlow) clearPnpSessionStorage();

          const cashDepositUrl = gatekeeper.navigation.getRoute(ROUTE_IDS.CASH_DEPOSIT) + '/'
          const redirectRoute = shouldRedirectToLobby ? '/' : cashDepositUrl;

          setRootLoading(false);
          setMetadata({ isOpen: false });
          setRootLoading(false);

          hasRegistered = true;
          window.location.href = redirectRoute;

          return;
        }

        const feCode = getFeErrorFromResponse(data);

        if (feCode === FE_ERROR_CODES.incorrect_details) {
          setRegisterError(FE_ERROR_CODES.incorrect_details);
          setRootLoading(false);

          return;
        }

        let errorData: Record<string, unknown> | undefined = parseBeError(data)?.cause;
        if (feCode === FE_ERROR_CODES.kyc_required) {
          const userId = data?.loginResponse?.playerId;
          const woId = formData?.welcomeOffer;
          if (userId) {
            setRegistrationEndNativeEvents({ userId, woId, kycRequired: true });
          }
          errorData = {
            token: data?.loginResponse?.identificationToken,
            pid:   userId,
          };
        }

        sendGtmEvent({
          feature: 'register',
          action:  'registration_blocked',
          data:    { 
            error_code:    response.status,
            error_message: feCode,
            market,
            userID:        data?.loginResponse?.playerId ?? data?.playerId,
            email:         passedData?.email ?? '',
          },
        });
        if (isPnpFlow) {
          const depositAmount = getDepositAmount();
          GtmEvents.logPnpRegistrationFailedCallback({
            status:       response.status,
            refundAmount: depositAmount ? Number(depositAmount) : undefined,
          });
        }

        setRootLoading(false);
        showBoundary(buildAppError(feCode, errorData));
        //eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        failedAttempts++;
        console.error('Error during registration:', err);

        const thrownBeError = parseBeError(err?.response?.data);
        let feCode: FeErrorCode = thrownBeError?.feCode ?? FE_ERROR_CODES.unknown_error;
        let errorData: Record<string, unknown> | undefined = thrownBeError?.cause;

        const beReason = thrownBeError
          ? undefined
          : err?.response?.data?.reason ??
            err?.response?.data?.loginResponse?.reason ??
            undefined;

        if (beReason) {
          feCode = mapBeReasonToFe(beReason);

          if (feCode === FE_ERROR_CODES.kyc_required) {
            const userId = err?.response?.data?.loginResponse?.playerId;
            const woId = formData?.welcomeOffer;
            if (userId) {
              setRegistrationEndNativeEvents({ userId, woId, kycRequired: true });
            }
            errorData = {
              token: err?.response?.data?.loginResponse?.identificationToken,
              pid:   userId,
            };
          }
        }

        if (attempt < MAX_RETRIES) {
          console.info(`Retrying request... Attempt ${attempt + 1}`);
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));

          return attemptRegistration(attempt + 1, seonSession);
        }

        sendGtmEvent({
          feature: 'register',
          action:  'registration_failed_callback',
          data:    { failedAttempts },
        });

        sendGtmEvent({
          feature: 'register',
          action:  'registration_blocked',
          data:    { 
            error_code:    err?.response?.status ?? 500,
            error_message: feCode,
            market,
            userID:        err?.response?.data?.loginResponse?.playerId ?? '',
            email:         passedData?.email ?? '',
          },
        });
        
        if (isPnpFlow) {
          const depositAmount = getDepositAmount();
          GtmEvents.logPnpRegistrationFailedCallback({
            status:       err?.response?.status ?? err?.status,
            refundAmount: depositAmount ? Number(depositAmount) : undefined,
            failedAttempts,
          });
        }
        GtmEvents.setRegistrationClosedWithErrorGtmEvent(failedAttempts, passedData || formData);

        setRootLoading(false);
        showBoundary(buildAppError(feCode, errorData));
      }
    };

    try {
      const seonSession = await getSeonSession();

      return await attemptRegistration(1, seonSession);
    } finally {
      if (!hasRegistered) setIsRegistering(false);
    }
  };

  return { registerUser, isRegistering };
};

export default useRegister;
